/**
 * DOM utilities to use native browser capabilities and avoid dependencies.
 *
 * @module dom.dom-util
 * @version 1.0.0
 * @since Sept 26 2019
 */

'use strict';

/**
 * Native querySelector to find first element in the dom with given selector
 *
 * @param  {string | Object} selector Target selector element.
 * @param  {string | Object} parent  Parent element, if it not provided
 * fallback to document.
 * @return {Object} returns the target as a DOM node
 */
const queryFirst = (exports.queryFirst = (selector, parent = document) => {
    if (!parent) return null;
    return parent.querySelector(selector);
});

/**
 * Native querySelectorAll to find all the dom element with given selector.
 *
 * @param  {string | Object} selector Target selector element.
 * @param  {string | Object} parent Parent element, if it not provided
 * fallback to document.
 * @return {nodeList | Array} Returns the target as a DOM node.
 */
const queryAll = (exports.queryAll = (selector, parent = document) => {
    if (!parent) return null;
    return [].slice.call(parent.querySelectorAll(selector), 0);
});

/**
 * Returns the value(s) for the specified CSS rule(s), or all computed styles if no rule is specified
 *
 * @param  {Element} element Target element
 * @param  {string} rule CSS rule name (E.g. padding-top)
 * @return {string | Array | Object} Returns a string value, array of values, or styles object
 */
const getStyles = (exports.getStyles = (element, ...rules) => {
    const styles = getComputedStyle(element);

    if (!rules.length) {
        return styles;
    } else if (rules.length === 1) {
        return styles[rules[0]];
    }
    return rules.map(rule => styles[rule]);
});

/**
 * Remove DOM element
 *
 * @param  {string | Object} el Target element.
 */
exports.remove = el => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.parentElement.removeChild(node);
        }
    } else {
        el.parentElement.removeChild(el);
    }
};

/**
 * Set attribute to single or multiple elements
 *
 * @param  {string | Object} el Target  element.
 * @param  {string} attr attribute that need to be added.
 * @param  {string | boolean} val value that need to be set for an attribute.
 */
exports.setAttribute = (el, attr, val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.setAttribute(attr, val);
        }
    } else {
        el.setAttribute(attr, val);
    }
};

/**
 * Remove attribute to single or multiple elements
 *
 * @param  {string | Object} el Target  element.
 * @param  {string} attr attribute that need to be removed.
 */
exports.removeAttribute = (el, attr) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.removeAttribute(attr);
        }
    } else {
        el.removeAttribute(attr);
    }
};

/**
 * Adds class or classes to the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.addClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.classList.add(...val);
        }
    } else {
        el.classList.add(...val);
    }
};

/**
 * Removes class or classes from the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.removeClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.classList.remove(...val);
        }
    } else {
        el.classList.remove(...val);
    }
};

/**
 * Toggles class or classes to the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 */
exports.toggleClass = (el, ...val) => {
    if (!el) return;
    if (Array.isArray(el) || el instanceof NodeList) {
        for (let i = 0, n = el.length; i < n; i++) {
            const node = el[i];
            node.classList.toggle(...val);
        }
    } else {
        el.classList.toggle(...val);
    }
};

/**
 * Looks for availability of class or classes in the classList of particular element.
 *
 * @param  {string | Object} el Target  element.
 * @param  {string | Array} val class name or class names as list of arguments.
 * @return {boolean} Returns availability of class in the target element.
 */
exports.hasClass = (el, ...classes) => {
    const { classList } = el;
    for (let c = 0, n = classes.length; c < n; c++) {
        if (!classList.contains(classes[c])) return false;
    }
    return true;
};

/**
 * Throttle util to prevent repeatative callbacks
 *
 * @param  {function} callback Callback function.
 * @param  {number} limit Timeout upon which throttle callback need to be called.
 * fallback to document.
 * @return {function} Throttle closure function.
 */
exports.throttle = (callback, limit = 250) => {
    let isThrottling = false;

    return (...args) => {
        if (isThrottling) return;

        callback.apply(this, args);
        isThrottling = true;

        setTimeout(() => {
            isThrottling = false;
        }, limit);
    };
};

/**
 * Throttle util to prevent repeatative callbacks
 *
 * @param  {function} callback Callback function.
 * @param  {number} minimumDelay Delay upon which debounce callback need to be called.
 * fallback to document.
 * @return {function} Debounce closure function.
 */
exports.debounce = (callback, minimumDelay = 250) => {
    let timer;

    return (...args) => {
        clearTimeout(timer);

        timer = setTimeout(() => {
            callback(...args);
        }, minimumDelay);
    };
};

exports.toArray = (...args) => {
    const makeArray = target => {
        let newTarget;
        if (!Array.isArray(target)) {
            const array = [].slice.call(target);
            newTarget = array.length ? array : [target];
        }
        return newTarget;
    };
    return args.reduce((results, arg) => {
        return results.concat(makeArray(arg));
    }, []);
};

/**
 * Height util to get outer height of element including margins
 *
 * @param  {string | Object} el target element
 * @return  {string} height of element
 */
exports.outerHeight = el => {
    const styles = getStyles(el);

    return el.offsetHeight + parseInt(styles.marginTop, 0) + parseInt(styles.marginBottom, 0);
};

/**
 * Determine if an element is in the viewport
 * (c) 2017 Chris Ferdinandi, MIT License, https://gomakethings.com
 * @param  {Node} el The element
 * @return {boolean} Returns true if element is in the viewport
 */
exports.isInViewport = el => {
    const scroll = window.scrollY || window.pageYOffset;
    const boundsTop = el.getBoundingClientRect().top + scroll;

    const numberOfViewportToCheck = el.dataset.nextViewportLazyLoad ? 2 : 1;

    const viewport = {
        top: scroll,
        bottom: scroll + window.innerHeight * numberOfViewportToCheck
    };

    const bounds = {
        top: boundsTop,
        bottom: boundsTop + el.clientHeight
    };

    return (bounds.bottom >= viewport.top && bounds.bottom <= viewport.bottom) || (bounds.top <= viewport.bottom && bounds.top >= viewport.top);
};

/**
 * Displays empty cart message
 * @param {string} message - Error message to display
 */
exports.emptyCart = message => {
    const cartElements = queryAll('.cart');
    cartElements.forEach(cartElement => {
        cartElement.innerHTML = `<div class="row cart-empty"><div class="col-12 text-center"><p>${message}</p></div></div>`;
    });
};

/**
 * Animates scrolling the page to the specified Y coordinate
 * @param {number} y - The Y coordinate to scroll to
 * @param {number} duration - The duration of the animation in milliseconds
 * @param {function} callback - The callback to run when the animation is complete
 * @returns {JQuery}
 */
exports.scrollTo = (y, duration = 600, callback = function() {}) => $('html, body').scrollTo(y, duration, callback);

/**
 * Finds the last element sibling on the same line as the specified element
 * @param {Element} element - The element to reference when looking for siblings
 * @returns {Element} - returns last element in the row
 */
exports.getLastElementInRow = element => {
    if (!element.nextElementSibling) return element;

    const bottom = element.offsetTop + element.scrollHeight;
    let sibling;

    while ((sibling = element.nextElementSibling)) {
        if (sibling.offsetTop >= bottom) return sibling.previousElementSibling;
        element = sibling;
    }

    return element;
};

/**
 * Update url with query parameter to handle remember placement
 *
 * @param {string} response - Ajax response HTML code
 * @return {undefined}
 */
exports.updateUrlQueryParam = response => {
    const $queryParamsElement = $('<div>')
        .append($(response))
        .find('.append-params');
    if ($queryParamsElement.length) {
        const newUrl = window.location.href.split('?')[0] + '?' + $queryParamsElement.val();
        window.history.pushState(
            {
                path: newUrl
            },
            '',
            newUrl
        );
    }
};

/**
 * This method returns character count
 * @param {HTMLElement} element - current element for which count has to be calculated
 * @return {number} character count
 */
exports.getCharacterCount = function(element) {
    if (!element) {
        return 0;
    }
    return element.value.length;
};

/**
 *  Waits for a specified element to exist.
 *  @param {string} selector - The selector to match
 *  @param {element} scope - The containing element to search within (default: document)
 *  @param {number} timeout - How long (in milliseconds) to wait before rejecting the promise
 *  @returns a Promise containing all matched elements
 */
exports.waitForElement = (selector, scope = document, timeout = 0) =>
    new Promise((resolve, reject) => {
        const elements = queryAll(selector);

        if (elements.length) return resolve(elements);

        const observer = new MutationObserver((mutations, self) => {
            const elements = queryAll(selector);

            if (elements.length) {
                self.disconnect();
                resolve(elements);
            }
        });

        observer.observe(scope, { childList: true, subtree: true });

        if (timeout) {
            setTimeout(() => {
                observer.disconnect();
                reject('Timed out');
            }, timeout);
        }
    });

exports.getStyles = getStyles;
exports.queryAll = queryAll;

/**
 * appends params to a url
 * @param {string} url - Original url
 * @param {Object} params - Parameters to append
 * @returns {string} result url with appended parameters
 */
exports.appendToUrl = (url, params) => {
    var newUrl = url;
    newUrl +=
        (newUrl.indexOf('?') !== -1 ? '&' : '?') +
        Object.keys(params)
            .map(function(key) {
                return key + '=' + encodeURIComponent(params[key]);
            })
            .join('&');

    return newUrl;
}
